/* eslint-disable no-console */
import * as THREE from 'three';
import { CreateScene } from './Load';
import {
  ControlPointObjectType,
  CreateDrainFunnelType,
  CustomParamsCropType,
  DataFileType,
  EditType,
  SaveSectorStorageType,
  SectionsObjetType,
} from '../types';
import { SectionsObject } from '../meshes/SectionsObject';
import { createDragControlsObject } from './DragObject';
import { DrawingSubstrate } from '../meshes/DrawingSubstrate';
import {
  EventAddSubstrate,
  EventChangeScaleObject,
  EventChangeScaleObjectType,
  EventChangeSector,
  EventChangeSectorType,
  EventRemoveDrainFunnel,
  EventUpdateDrainFunnel,
} from '../../helpers/events';
import { LineObject } from '../meshes/LineObject';
import { SetScaleObject } from '../meshes/SetScale/SetScaleObject';
import { SupportsGroup } from '../meshes/CalcSupports/SupportsGrpoup';
import { ConstructorSupportsProps } from '../meshes/CalcSupports/types';
import { SupportGroupTimber } from '../meshes/CalcSupports/SupportGroupTimber';
import { DrainLineObject } from '../meshes/DrainLine/DrainLineObject';
import { DrainFunnelObject } from '../meshes/DrainFunnelObject';
import { SlopedSurface } from '../meshes/SlopedSurface';
import { addNotificationDispatch } from '../js';

const createEdit = (): EditType => {
  return {
    linesSection: [],
    sectionsObjects: null,
    dragObject: null,
    scaleCoefficient: 1,
    drawingSubstrate: null,
    drainFunnel: null,
    lineObject: null,
    totalPointHeight: 0.15,
    setScaleObject: null,
    supportsGroup: null,
    supportsGroupTimber: null,
    selectModificationSupport: 'basicStandard',
    slopedSurface: null,
    Load: null,
    temporaryParamsSupportsObjects: null,

    addLoad(contCanvas: HTMLDivElement) {
      this.Load = new CreateScene();
      this.Load.initScene(contCanvas);
      Edit.activateDragObject();
    },
    removeLoad() {
      if (this.Load) {
        this.Load.deactivate();
        this.clearSceneAndRemoveLoad();
        this.Load = null;

        this.deactivateDragObject();
      }
    },
    changeScaleCoefficient(originSize: number, currentSize: number) {
      this.scaleCoefficient = currentSize / originSize;
      this.sectionsObjects?.updateDimensions();
    },
    activateDragObject() {
      if (this.Load?.camera && this.Load?.controls) {
        if (!this.dragObject) {
          this.dragObject = createDragControlsObject({
            camera: this.Load.camera,
            renderer: this.Load.renderer,
            orbitControls: this.Load.controls,
          });
        }
        this.dragObject.activateControls();
      }
    },
    deactivateDragObject() {
      if (this.dragObject) {
        this.dragObject.deactivateControls();
        this.dragObject = null;
      }
    },
    addingSector() {
      if (this.Load) {
        const section = new SectionsObject(this.Load, this);
        this.sectionsObjects = section;
        section.buildSector();
        this.Load.scene.add(section);
        document.dispatchEvent(
          new CustomEvent<EventChangeSectorType>(EventChangeSector, { detail: { isSector: true } })
        );
      }
    },
    saveSector() {
      if (this.sectionsObjects) {
        const sector: SaveSectorStorageType = {
          points: [],
          pointsBridge: [],
          totalPointHeight: this.totalPointHeight,
        };
        this.sectionsObjects.controlPoints.forEach(item => {
          sector.points.push({
            x: item.x,
            y: item.y,
            z: item.z,
          });
        });
        this.sectionsObjects.bridges.forEach(item => {
          sector.pointsBridge.push({
            start: item.start.numberInLine,
            end: item.end.numberInLine,
          });
        });
        if (this.drainFunnel && this.drainFunnel instanceof DrainFunnelObject) {
          sector.drainFunnelObjects = {
            type: 'point',
            objects: [],
          };
          this.drainFunnel.drainObjects.forEach(item => {
            sector.drainFunnelObjects?.objects.push({
              roomDottedName: item.planeIntersected.name,
              position: {
                x: item.position.x,
                y: item.position.y,
                z: item.position.z,
              },
            });
          });
        } //
        else if (this.drainFunnel && this.drainFunnel instanceof DrainLineObject) {
          sector.drainFunnelObjects = {
            type: 'line',
            objects: [],
          };
          this.drainFunnel.planesIntersected.forEach(item => {
            sector.drainFunnelObjects?.objects?.push({
              roomDottedName: item.name,
              position: {
                x: item.drainFunnelLine?.start.x || 0,
                y: item.drainFunnelLine?.start.y || 0,
                z: item.drainFunnelLine?.start.z || 0,
              },
              end: {
                x: item.drainFunnelLine?.end.x || 0,
                y: item.drainFunnelLine?.end.y || 0,
                z: item.drainFunnelLine?.end.z || 0,
              },
            });
          });
        } //
        else if (this.slopedSurface) {
          sector.slopedSurface = {
            angleSloped: this.slopedSurface.angleSloped,
            direction: this.slopedSurface.direction,
          };
        }
        if (this.temporaryParamsSupportsObjects) {
          sector.calculationResult = { ...this.temporaryParamsSupportsObjects };
        }
        // console.log(sector);
        return JSON.stringify(sector);
      } //
      else {
        addNotificationDispatch('Для сохранения проекта необходимо создать сектор', 'error');
      }
    },
    restoreSector(sector, substrateImage) {
      if (substrateImage) {
        this.addingDrawing(substrateImage);
        setTimeout(() => {
          if (
            this.drawingSubstrate &&
            !(substrateImage instanceof File) &&
            substrateImage.paramsPruning
          ) {
            const isRebuild = defineIsRebuildSubstrate(substrateImage.paramsPruning);
            if (isRebuild) {
              this.drawingSubstrate.runCropAction(substrateImage.paramsPruning);
            }
            if (substrateImage.paramsPruning.coefficient) {
              this.drawingSubstrate.updateScale(substrateImage.paramsPruning.coefficient);
            }
          }
        }, 200);
      }
      if (sector && !this.sectionsObjects) {
        const points = [];
        const dataSave = getDataParse(sector) as SaveSectorStorageType;
        if (dataSave.points && this.Load) {
          for (let i = 0; i < dataSave.points.length; i++) {
            points.push(
              new THREE.Vector3(dataSave.points[i].x, dataSave.points[i].y, dataSave.points[i].z)
            );
          }

          const section = new SectionsObject(this.Load, this);
          section.controlPoints = points;

          this.sectionsObjects = section;
          section.buildSector();
          this.Load.scene.add(section);

          for (let i = 0; i < dataSave.pointsBridge.length; i++) {
            const start =
              this.sectionsObjects.controlsPointsObjects[dataSave.pointsBridge[i].start];
            const end = this.sectionsObjects.controlsPointsObjects[dataSave.pointsBridge[i].end];
            this.sectionsObjects.bridges.push({ start, end });
            this.sectionsObjects.createBridgeLine(start, end);
          }
          this.sectionsObjects.createRooms();
          this.totalPointHeight = dataSave.totalPointHeight;
          if (dataSave?.drainFunnelObjects?.objects?.length) {
            this.addDrainFunnel(dataSave.drainFunnelObjects.type, dataSave.drainFunnelObjects);
          }
          if (dataSave?.slopedSurface) {
            this.createSlopedSurface(
              dataSave.slopedSurface.direction,
              dataSave.slopedSurface.angleSloped
            );
          }
          if (dataSave?.calculationResult) {
            this.addSupportsObjects(dataSave.calculationResult);
          }

          // setTimeout(() => {
          //   if (this.drawingSubstrate && dataSave.drawingSubstrate) {
          //     this.drawingSubstrate.runCropAction(dataSave.drawingSubstrate);
          //   }
          // }, 200);

          document.dispatchEvent(
            new CustomEvent<EventChangeSectorType>(EventChangeSector, {
              detail: { isSector: true },
            })
          );
        }
      }
    },
    removeSector() {
      if (this.sectionsObjects) {
        this.sectionsObjects.removeSector();
        this.sectionsObjects = null;
        document.dispatchEvent(
          new CustomEvent<EventChangeSectorType>(EventChangeSector, { detail: { isSector: false } })
        );
      }
    },
    addDrainFunnel(
      type: CreateDrainFunnelType,
      savedDrainFunnel?: SaveSectorStorageType['drainFunnelObjects']
    ): boolean {
      if (this.Load && this.sectionsObjects) {
        if (type === 'line') {
          this.drainFunnel = new DrainLineObject({
            points: this.sectionsObjects.controlPoints,
            rooms: this.sectionsObjects.rooms,
            Edit: this,
            canvasListener: this.Load?.domElementListeners,
            savedDrainFunnel,
          });
        } else {
          this.drainFunnel = new DrainFunnelObject({
            points: this.sectionsObjects.controlPoints,
            rooms: this.sectionsObjects.rooms,
            Edit: this,
            canvasListener: this.Load?.domElementListeners,
            savedDrainFunnel,
          });
        }
        this.Load.scene.add(this.drainFunnel);
        dispatchEvent(new CustomEvent(EventUpdateDrainFunnel));
        return true;
      }
      return false;
    },
    removeDrainFunnel() {
      if (this.drainFunnel) {
        this.drainFunnel.removeGroup();
        this.drainFunnel = null;
        dispatchEvent(new CustomEvent(EventRemoveDrainFunnel));
      }
    },
    addingDrawing(file: DataFileType) {
      if (this.Load && file) {
        const drawing = new DrawingSubstrate(file);
        this.Load.scene.add(drawing);
        drawing.position.setY(-0.05);
        // console.log(drawing)
        this.drawingSubstrate = drawing;
        this.Load.drawingSubstrate = drawing;
        document.dispatchEvent(new CustomEvent(EventAddSubstrate));
      }
    },
    removeDrawing() {
      if (this.drawingSubstrate) {
        this.drawingSubstrate.removeMesh();
        this.drawingSubstrate = null;
        if (this.Load?.drawingSubstrate) {
          this.Load.drawingSubstrate = null;
        }
      }
    },
    changeSideLength(symbolControlPoint: string, length: number, direct: 'forward' | 'back'): void {
      if (this.Load) {
        const section = this.Load.scene.getObjectByName('section_object') as SectionsObjetType;
        if (section) {
          const controlPoint = section.controlsPointsObjects.find(
            item => item.textObject?.text === symbolControlPoint
          ) as ControlPointObjectType;
          if (controlPoint) {
            if (direct === 'back') {
              const p1 = controlPoint.linkPointEnd?.lineObject.startPoint.position.clone();
              const p2 = controlPoint.linkPointEnd?.lineObject.endPoint.position.clone();
              if (p1 && p2) {
                const dir = p2.clone().sub(p1).normalize();
                const dist = length / this.scaleCoefficient - p1.distanceTo(p2);
                controlPoint.dragStart();
                controlPoint.position.add(dir.clone().multiplyScalar(dist));
                controlPoint.drag(true);
                controlPoint.dragEnd();
              }
            } else {
              const p1 = controlPoint.linkPointStart?.lineObject.startPoint.position.clone();
              const p2 = controlPoint.linkPointStart?.lineObject.endPoint.position.clone();
              if (p1 && p2) {
                const dir = p1.clone().sub(p2).normalize();
                const dist = length / this.scaleCoefficient - p1.distanceTo(p2);
                controlPoint.dragStart();
                controlPoint.position.add(dir.clone().multiplyScalar(dist));
                controlPoint.drag(true);
                controlPoint.dragEnd();
              }
            }
          }
        }
      }
    },
    createLineObject(controlPoint: ControlPointObjectType) {
      const startPoint = controlPoint.position.clone();
      const endPoint = startPoint.clone().add(new THREE.Vector3(0, 0, 0.05));
      const intersected: ControlPointObjectType[] = [];
      if (this.sectionsObjects) {
        for (let i = 0; i < this.sectionsObjects.rooms.length; i++) {
          if (this.sectionsObjects.rooms[i].includes(controlPoint.numberInLine)) {
            this.sectionsObjects.rooms[i].forEach(item => {
              if (this.sectionsObjects) {
                intersected.push(this.sectionsObjects.controlsPointsObjects[item]);
              }
            });
            break;
          }
        }
      }

      if (this.Load) {
        const stepLine = new LineObject({
          startPoint,
          endPoint,
          intersectedCircles: intersected,
          startObject: controlPoint,
        });
        this.Load.scene.add(stepLine);
        this.lineObject = stepLine;
      }
    },
    removeLineObject(isCreateBridgeLine) {
      if (this.lineObject) {
        if (!isCreateBridgeLine) {
          this.lineObject.removeLineObject();
          this.lineObject = null;
          return;
        }

        const start = this.lineObject.startObject;
        const end = this.lineObject.selectIntersectedObject;
        if (end?.linkPointBridge) {
          this.lineObject.removeLineObject();
          this.lineObject = null;
          return;
        }
        if (this.sectionsObjects && start && end) {
          if (start.numberInLine < end.numberInLine) {
            this.sectionsObjects.bridges.push({ start, end });
            this.sectionsObjects.createBridgeLine(start, end);
          } else if (start.numberInLine > end.numberInLine) {
            this.sectionsObjects.bridges.push({ end, start });
            this.sectionsObjects.createBridgeLine(end, start);
          } else {
            return;
          }
        }

        this.sectionsObjects?.createRooms();

        // console.log(this.lineObject);
        this.lineObject.removeLineObject();
        this.lineObject = null;
      }
    },
    changeTotalPointHeight(value: number) {
      this.totalPointHeight = value;
      if (this.sectionsObjects) {
        this.sectionsObjects.updateHeightControlPoints();
      }
      if (this.drainFunnel) {
        this.drainFunnel.updateHeightControlPoints();
      }
    },
    addSetScaleObject() {
      const scaleObj = new SetScaleObject({ edit: this });
      this.Load?.scene.add(scaleObj);
      this.setScaleObject = scaleObj;
      document.dispatchEvent(
        new CustomEvent<EventChangeScaleObjectType>(EventChangeScaleObject, {
          detail: { isObjectScale: true },
        })
      );
    },
    removeSetScaleObject() {
      if (this.setScaleObject) {
        this.setScaleObject.removeGroup();
        this.setScaleObject = null;
        document.dispatchEvent(
          new CustomEvent<EventChangeScaleObjectType>(EventChangeScaleObject, {
            detail: { isObjectScale: false },
          })
        );
      }
    },
    addSupportsObjects({
      depthTile,
      offsetX,
      offsetZ,
      customerOffsetX,
      customerOffsetZ,
      directionBuildDeg,
    }) {
      this.temporaryParamsSupportsObjects = {
        depthTile,
        offsetX,
        offsetZ,
        customerOffsetX,
        customerOffsetZ,
        directionBuildDeg,
      };
      if (this.sectionsObjects?.controlPoints.length) {
        let props: ConstructorSupportsProps;
        let supportsGroup;

        if (this.drainFunnel) {
          props = {
            contour: this.sectionsObjects.controlPoints,
            roomsObjects: this.drainFunnel.planesIntersected,
            offsetX,
            offsetZ,
            selectModificationSupport: this.selectModificationSupport,
            customerOffsetX,
            customerOffsetZ,
            depthTile,
          };
        } //
        else if (this.slopedSurface) {
          props = {
            contour: this.sectionsObjects.controlPoints,
            roomsObjects: [this.slopedSurface],
            offsetX,
            offsetZ,
            selectModificationSupport: this.selectModificationSupport,
            customerOffsetX,
            customerOffsetZ,
            depthTile,
          };
        } //
        else {
          props = {
            contour: this.sectionsObjects.controlPoints,
            rooms: this.sectionsObjects.rooms,
            offsetX,
            offsetZ,
            customerOffsetX,
            customerOffsetZ,
            depthTile,
            selectModificationSupport: this.selectModificationSupport,
          };
        }

        if (directionBuildDeg !== undefined) {
          props.directionBuildDeg = directionBuildDeg;
          supportsGroup = new SupportGroupTimber({ ...props });
          this.supportsGroupTimber = supportsGroup;
        } //
        else {
          supportsGroup = new SupportsGroup(props);
          this.supportsGroup = supportsGroup;
        }

        this.Load?.scene.add(supportsGroup);
      }
    },
    removeSupportsObjects() {
      this.temporaryParamsSupportsObjects = null;
      if (this.supportsGroup) {
        this.supportsGroup.removeGroup();
        this.supportsGroup = null;
      }
      if (this.supportsGroupTimber) {
        this.supportsGroupTimber.removeGroup();
        this.supportsGroupTimber = null;
      }
    },
    createSlopedSurface(direction, angleSloped): boolean {
      if (this.sectionsObjects) {
        const sloped = new SlopedSurface({
          contour: this.sectionsObjects.controlPoints,
          angleSloped: angleSloped ?? 0,
          direction: direction ?? 'topBottom',
        });
        this.Load?.scene.add(sloped);
        this.slopedSurface = sloped;
        return true;
      }
      return false;
    },
    removeSlopedSurface() {
      if (this.slopedSurface) {
        this.slopedSurface.removeMesh();
        this.slopedSurface = null;
        dispatchEvent(new CustomEvent(EventRemoveDrainFunnel));
      }
    },
    clearSceneAndRemoveLoad() {
      this.removeDrainFunnel();
      this.removeSlopedSurface();
      this.removeSupportsObjects();
      this.removeSetScaleObject();
      this.removeSector();
      this.removeDrawing();
    },
  };

  function defineIsRebuildSubstrate(data: CustomParamsCropType): boolean {
    const _data = { ...data };
    delete _data.coefficient;
    let value = 0;
    for (const dataKey in _data) {
      value += _data[dataKey as keyof CustomParamsCropType] ?? 0;
    }

    return value > 0;
  }
  function getDataParse(str: string) {
    let count = 0;
    let currentStr = JSON.parse(str);
    while (typeof currentStr === 'string' && count < 20) {
      currentStr = JSON.parse(currentStr);
      count += 1;
    }

    return currentStr;
  }
};

export const Edit = createEdit();
